import React, {
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';

import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { ContainerClient } from '@azure/storage-blob';

/* =========================================================
   CONFIGURATION
   ========================================================= */

/*
 * Replace this with a current SAS token for:
 *
 * Storage account: oppiesadminweb
 * Container: shop-imports
 *
 * Do not include a leading question mark.
 */
const SHOP_IMPORT_SAS_TOKEN =
    'sv=2026-02-06&ss=bfqt&srt=sco&sp=rwdlacupiytfx&se=2026-11-06T02:52:49Z&st=2026-10-22T18:37:49Z&spr=https,http&sig=rQH5O2g5%2Fa4yZKsfIw72JN1Cz1LHQAn2Dj%2BpUwYhezo%3D';

const STORAGE_ACCOUNT = 'oppiesadminweb';
const CONTAINER_NAME = 'shop-imports';

const SEARCH_ADDRESS_URL =
    'https://oppiesfunc.azurewebsites.net/api/searchShopAddresses?code=X0-m7ol4AEMcwVG0HdYiAidPss9PkGoZdcYTC8aOCn2SAzFuuLkOKw==';

const CREATE_MANUAL_SHOP_URL =
    'https://oppiesfunc.azurewebsites.net/api/createManualShop?code=mVnLbnh4jmvtMsHl75W-HR5BRqyLHUPKM0h7839WXflIAzFuSPjoeQ==';

const DAYS = [
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
    'Sunday'
];

/* =========================================================
   INITIAL DATA
   ========================================================= */

const createEmptyHours = () =>
    DAYS.reduce((result, day) => {
        result[day] = '';
        return result;
    }, {});

const createInitialForm = () => ({
    name: '',
    address: '',
    contact: '',
    email: '',
    suburb: '',
    latitude: '',
    longitude: '',
    placeId: '',
    hours: createEmptyHours()
});

/* =========================================================
   HELPERS
   ========================================================= */

function createSlug(value) {
    return String(value || '')
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .replace(/-{2,}/g, '-');
}

function escapeCsvValue(value) {
    if (value === null || value === undefined) {
        return '';
    }

    const stringValue =
        typeof value === 'object'
            ? JSON.stringify(value)
            : String(value);

    return `"${stringValue.replace(/"/g, '""')}"`;
}

function normaliseAddressSuggestions(responseData) {
    const source =
        responseData?.suggestions ||
        responseData?.predictions ||
        responseData?.results ||
        [];

    if (!Array.isArray(source)) {
        return [];
    }

    return source
        .map((item) => ({
            placeId:
                item.placeId ||
                item.place_id ||
                item.id ||
                '',

            description:
                item.description ||
                item.formattedAddress ||
                item.formatted_address ||
                item.address ||
                item.text ||
                '',

            mainText:
                item.mainText ||
                item.main_text ||
                item.structured_formatting?.main_text ||
                '',

            secondaryText:
                item.secondaryText ||
                item.secondary_text ||
                item.structured_formatting?.secondary_text ||
                ''
        }))
        .filter((item) => item.description);
}

function getFriendlyError(error, fallbackMessage) {
    if (error?.name === 'AbortError') {
        return '';
    }

    return (
        error?.message ||
        fallbackMessage ||
        'Something went wrong.'
    );
}

/* =========================================================
   COMPONENT
   ========================================================= */

function ShopImporter() {
    const addressWrapperRef = useRef(null);
    const searchAbortControllerRef = useRef(null);

    const [activeMode, setActiveMode] =
        useState('manual');

    const [manualForm, setManualForm] =
        useState(createInitialForm);

    const [manualSaving, setManualSaving] =
        useState(false);

    const [manualStatus, setManualStatus] =
        useState('');

    const [manualStatusType, setManualStatusType] =
        useState('');

    const [addressSuggestions, setAddressSuggestions] =
        useState([]);

    const [addressSearching, setAddressSearching] =
        useState(false);

    const [showAddressSuggestions, setShowAddressSuggestions] =
        useState(false);

    const [selectedAddressConfirmed, setSelectedAddressConfirmed] =
        useState(false);

    const [uploading, setUploading] =
        useState(false);

    const [uploadStatus, setUploadStatus] =
        useState('');

    const [uploadStatusType, setUploadStatusType] =
        useState('');

    const generatedShopId = useMemo(() => {
        return createSlug(manualForm.name);
    }, [manualForm.name]);

    /* =====================================================
       CLOSE AUTOCOMPLETE WHEN CLICKING OUTSIDE
       ===================================================== */

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (
                addressWrapperRef.current &&
                !addressWrapperRef.current.contains(
                    event.target
                )
            ) {
                setShowAddressSuggestions(false);
            }
        };

        document.addEventListener(
            'mousedown',
            handleOutsideClick
        );

        return () => {
            document.removeEventListener(
                'mousedown',
                handleOutsideClick
            );
        };
    }, []);

    /* =====================================================
       ADDRESS AUTOCOMPLETE
       ===================================================== */

    useEffect(() => {
        const query = manualForm.address.trim();

        if (
            activeMode !== 'manual' ||
            selectedAddressConfirmed ||
            query.length < 3
        ) {
            setAddressSuggestions([]);
            setAddressSearching(false);
            return;
        }

        const timer = setTimeout(async () => {
            if (searchAbortControllerRef.current) {
                searchAbortControllerRef.current.abort();
            }

            const controller = new AbortController();
            searchAbortControllerRef.current = controller;

            setAddressSearching(true);

            try {
                const response = await fetch(
                    `${SEARCH_ADDRESS_URL}&query=${encodeURIComponent(
                        query
                    )}`,
                    {
                        method: 'GET',
                        headers: {
                            Accept: 'application/json'
                        },
                        signal: controller.signal
                    }
                );

                let responseData = null;

                try {
                    responseData = await response.json();
                } catch {
                    responseData = null;
                }

                if (!response.ok) {
                    throw new Error(
                        responseData?.error ||
                        responseData?.message ||
                        `Address search failed with status ${response.status}.`
                    );
                }

                const suggestions =
                    normaliseAddressSuggestions(
                        responseData
                    );

                setAddressSuggestions(suggestions);
                setShowAddressSuggestions(
                    suggestions.length > 0
                );
            } catch (error) {
                if (error?.name !== 'AbortError') {
                    console.error(
                        'Address search failed:',
                        error
                    );

                    setAddressSuggestions([]);
                    setShowAddressSuggestions(false);
                }
            } finally {
                if (!controller.signal.aborted) {
                    setAddressSearching(false);
                }
            }
        }, 350);

        return () => {
            clearTimeout(timer);
        };
    }, [
        activeMode,
        manualForm.address,
        selectedAddressConfirmed
    ]);

    /* =====================================================
       MANUAL FORM HANDLERS
       ===================================================== */

    const updateManualField = (event) => {
        const { name, value } = event.target;

        setManualForm((current) => ({
            ...current,
            [name]: value
        }));

        setManualStatus('');
        setManualStatusType('');

        if (name === 'address') {
            setSelectedAddressConfirmed(false);

            setManualForm((current) => ({
                ...current,
                address: value,
                placeId: '',
                latitude: '',
                longitude: '',
                suburb: ''
            }));
        }
    };

    const updateHours = (day, value) => {
        setManualForm((current) => ({
            ...current,
            hours: {
                ...current.hours,
                [day]: value
            }
        }));

        setManualStatus('');
        setManualStatusType('');
    };

    const selectAddressSuggestion = (suggestion) => {
        setManualForm((current) => ({
            ...current,
            address: suggestion.description,
            placeId: suggestion.placeId || '',
            latitude: '',
            longitude: '',
            suburb: ''
        }));

        setSelectedAddressConfirmed(true);
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        setManualStatus('');
        setManualStatusType('');
    };

    const clearManualForm = () => {
        if (searchAbortControllerRef.current) {
            searchAbortControllerRef.current.abort();
        }

        setManualForm(createInitialForm());
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
        setSelectedAddressConfirmed(false);
        setAddressSearching(false);
        setManualStatus('');
        setManualStatusType('');
    };

    const buildManualShopPayload = () => {
        const cleanedHours = DAYS.reduce(
            (result, day) => {
                const value =
                    manualForm.hours[day]?.trim();

                if (value) {
                    result[day] = value;
                }

                return result;
            },
            {}
        );

        return {
            shopId:
                generatedShopId ||
                `opshop-${uuidv4().slice(0, 8)}`,

            name: manualForm.name.trim(),

            address: manualForm.address.trim(),

            placeId:
                manualForm.placeId.trim() || null,

            contact: manualForm.contact.trim(),

            email:
                manualForm.email.trim() ||
                'admin@import',

            suburb:
                manualForm.suburb.trim() || null,

            latitude:
                manualForm.latitude === ''
                    ? null
                    : Number(manualForm.latitude),

            longitude:
                manualForm.longitude === ''
                    ? null
                    : Number(manualForm.longitude),

            hours: cleanedHours,

            role: 'OpShop',
            approved: true,
            source: 'admin-portal',
            createdBy: 'admin-portal'
        };
    };

    const submitManualShop = async (event) => {
        event.preventDefault();

        setManualSaving(true);
        setManualStatus('');
        setManualStatusType('');

        try {
            const payload =
                buildManualShopPayload();

            const response = await fetch(
                CREATE_MANUAL_SHOP_URL,
                {
                    method: 'POST',

                    headers: {
                        'Content-Type':
                            'application/json',
                        Accept: 'application/json'
                    },

                    body: JSON.stringify(payload)
                }
            );

            let responseData = null;

            try {
                responseData = await response.json();
            } catch {
                responseData = null;
            }

            if (!response.ok) {
                throw new Error(
                    responseData?.error ||
                    responseData?.message ||
                    `Create shop failed with status ${response.status}.`
                );
            }

            const createdShop =
                responseData?.shop ||
                responseData?.document ||
                responseData;

            const createdName =
                createdShop?.name ||
                payload.name ||
                payload.shopId;

            const createdId =
                createdShop?.shopId ||
                createdShop?.id ||
                payload.shopId;

            setManualStatus(
                `${createdName} was added successfully. Shop ID: ${createdId}`
            );

            setManualStatusType('success');

            setManualForm(createInitialForm());
            setSelectedAddressConfirmed(false);
            setAddressSuggestions([]);
            setShowAddressSuggestions(false);
        } catch (error) {
            console.error(
                'Manual shop creation failed:',
                error
            );

            setManualStatus(
                getFriendlyError(
                    error,
                    'The Op Shop could not be created.'
                )
            );

            setManualStatusType('error');
        } finally {
            setManualSaving(false);
        }
    };

    /* =====================================================
       CSV IMPORT
       ===================================================== */

    const uploadToBlobStorage = async (file) => {
        setUploading(true);
        setUploadStatus('');
        setUploadStatusType('');

        const cleanSasToken =
            SHOP_IMPORT_SAS_TOKEN.replace(/^\?/, '');

        if (
            !cleanSasToken ||
            cleanSasToken ===
            'REPLACE_WITH_CURRENT_SHOP_IMPORT_SAS_TOKEN'
        ) {
            setUploadStatus(
                'The Shop Import SAS token has not been configured.'
            );

            setUploadStatusType('error');
            setUploading(false);
            return;
        }

        const fileName =
            `shop-import-${Date.now()}-${uuidv4()}.csv`;

        const containerUrl =
            `https://${STORAGE_ACCOUNT}` +
            `.blob.core.windows.net/` +
            `${CONTAINER_NAME}?${cleanSasToken}`;

        try {
            /*
             * The original page used BlobServiceClient with a
             * container URL. ContainerClient is the correct
             * client for a container-level SAS URL.
             */
            const containerClient =
                new ContainerClient(containerUrl);

            const blockBlobClient =
                containerClient.getBlockBlobClient(
                    fileName
                );

            await blockBlobClient.uploadBrowserData(
                file,
                {
                    blobHTTPHeaders: {
                        blobContentType:
                            file.type ||
                            'text/csv;charset=utf-8'
                    },

                    metadata: {
                        source: 'admin-portal',
                        importType: 'bulk-shop-import'
                    }
                }
            );

            setUploadStatus(
                `Upload complete. File: ${fileName}`
            );

            setUploadStatusType('success');
        } catch (error) {
            console.error(
                'CSV upload failed:',
                error
            );

            setUploadStatus(
                getFriendlyError(
                    error,
                    'The CSV file could not be uploaded.'
                )
            );

            setUploadStatusType('error');
        } finally {
            setUploading(false);
        }
    };

    const onDrop = async (
        acceptedFiles,
        rejectedFiles
    ) => {
        if (rejectedFiles?.length) {
            setUploadStatus(
                'Please select one valid CSV file.'
            );

            setUploadStatusType('error');
            return;
        }

        if (!acceptedFiles?.length) {
            return;
        }

        await uploadToBlobStorage(
            acceptedFiles[0]
        );
    };

    const downloadTemplate = () => {
        const exampleHours = {
            Monday: '9am - 5pm',
            Tuesday: '9am - 5pm',
            Wednesday: '9am - 5pm',
            Thursday: '9am - 5pm',
            Friday: '9am - 5pm',
            Saturday: '10am - 4pm',
            Sunday: 'Closed'
        };

        const headers = [
            'shopId',
            'name',
            'address',
            'contact',
            'email',
            'hours',
            'latitude',
            'longitude',
            'suburb',
            'role',
            'approved'
        ];

        const exampleRow = [
            'retro-threads-auckland',
            'Retro Threads',
            '123 Main Street, Auckland 1010',
            '09 123 4567',
            'admin@import',
            exampleHours,
            '-36.8485',
            '174.7633',
            'Auckland Central',
            'OpShop',
            'true'
        ];

        const csvContent = [
            headers.join(','),
            exampleRow
                .map(escapeCsvValue)
                .join(',')
        ].join('\r\n');

        const csvBlob = new Blob(
            ['\uFEFF', csvContent],
            {
                type:
                    'text/csv;charset=utf-8'
            }
        );

        const objectUrl =
            window.URL.createObjectURL(csvBlob);

        const downloadLink =
            document.createElement('a');

        downloadLink.href = objectUrl;
        downloadLink.download =
            'opshops-import-template.csv';

        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);

        window.URL.revokeObjectURL(objectUrl);
    };

    const {
        getRootProps,
        getInputProps,
        isDragActive
    } = useDropzone({
        onDrop,

        accept: {
            'text/csv': ['.csv'],
            'application/csv': ['.csv'],
            'application/vnd.ms-excel': ['.csv']
        },

        multiple: false,
        disabled: uploading
    });

    /* =====================================================
       RENDER
       ===================================================== */

    return (
        <div style={styles.page}>
            <div style={styles.container}>
                <div style={styles.header}>
                    <div>
                        <h2 style={styles.title}>
                            Import Op Shops
                        </h2>

                        <p style={styles.subtitle}>
                            Add one Op Shop manually or
                            import multiple shops from CSV.
                        </p>
                    </div>
                </div>

                <div style={styles.tabs}>
                    <button
                        type="button"
                        onClick={() => {
                            setActiveMode('manual');
                            setUploadStatus('');
                            setUploadStatusType('');
                        }}
                        style={{
                            ...styles.tab,
                            ...(activeMode === 'manual'
                                ? styles.activeTab
                                : {})
                        }}
                    >
                        Add One Op Shop
                    </button>

                    <button
                        type="button"
                        onClick={() => {
                            setActiveMode('bulk');
                            setManualStatus('');
                            setManualStatusType('');
                            setShowAddressSuggestions(
                                false
                            );
                        }}
                        style={{
                            ...styles.tab,
                            ...(activeMode === 'bulk'
                                ? styles.activeTab
                                : {})
                        }}
                    >
                        Import CSV
                    </button>
                </div>

                {activeMode === 'manual' && (
                    <form
                        onSubmit={submitManualShop}
                        style={styles.form}
                    >
                        <div style={styles.sectionTitle}>
                            Shop details
                        </div>

                        <label style={styles.label}>
                            <span style={styles.labelText}>
                                Op Shop
                            </span>

                            <input
                                type="text"
                                name="name"
                                value={manualForm.name}
                                onChange={updateManualField}
                                placeholder="Red Cross Karangahape Road"
                                style={styles.input}
                            />
                        </label>

                        {generatedShopId && (
                            <div style={styles.shopIdPreview}>
                                Shop ID: {generatedShopId}
                            </div>
                        )}

                        <div
                            ref={addressWrapperRef}
                            style={styles.addressWrapper}
                        >
                            <label style={styles.label}>
                                <span style={styles.labelText}>
                                    Address
                                </span>

                                <div
                                    style={
                                        styles.addressInputWrapper
                                    }
                                >
                                    <input
                                        type="text"
                                        name="address"
                                        value={
                                            manualForm.address
                                        }
                                        onChange={
                                            updateManualField
                                        }
                                        onFocus={() => {
                                            if (
                                                addressSuggestions.length >
                                                0
                                            ) {
                                                setShowAddressSuggestions(
                                                    true
                                                );
                                            }
                                        }}
                                        placeholder="Start typing a New Zealand or Australian address"
                                        autoComplete="off"
                                        style={{
                                            ...styles.input,
                                            paddingRight: 42
                                        }}
                                    />

                                    {addressSearching && (
                                        <span
                                            style={
                                                styles.addressLoader
                                            }
                                        >
                                            …
                                        </span>
                                    )}
                                </div>
                            </label>

                            {showAddressSuggestions &&
                                addressSuggestions.length >
                                0 && (
                                    <div
                                        style={
                                            styles.suggestions
                                        }
                                    >
                                        {addressSuggestions.map(
                                            (
                                                suggestion,
                                                index
                                            ) => (
                                                <button
                                                    type="button"
                                                    key={
                                                        suggestion.placeId ||
                                                        `${suggestion.description}-${index}`
                                                    }
                                                    onClick={() =>
                                                        selectAddressSuggestion(
                                                            suggestion
                                                        )
                                                    }
                                                    style={
                                                        styles.suggestionButton
                                                    }
                                                >
                                                    <span
                                                        style={
                                                            styles.suggestionMain
                                                        }
                                                    >
                                                        {suggestion.mainText ||
                                                            suggestion.description}
                                                    </span>

                                                    {suggestion.secondaryText && (
                                                        <span
                                                            style={
                                                                styles.suggestionSecondary
                                                            }
                                                        >
                                                            {
                                                                suggestion.secondaryText
                                                            }
                                                        </span>
                                                    )}
                                                </button>
                                            )
                                        )}
                                    </div>
                                )}
                        </div>

                        {selectedAddressConfirmed && (
                            <div
                                style={
                                    styles.confirmedAddress
                                }
                            >
                                Address selected. Geographic
                                details and country will be
                                confirmed by oppiesfunc when
                                the shop is saved.
                            </div>
                        )}

                        <div style={styles.twoColumns}>
                            <label style={styles.label}>
                                <span
                                    style={styles.labelText}
                                >
                                    Contact
                                </span>

                                <input
                                    type="text"
                                    name="contact"
                                    value={
                                        manualForm.contact
                                    }
                                    onChange={
                                        updateManualField
                                    }
                                    placeholder="09 377 8072"
                                    style={styles.input}
                                />
                            </label>

                            <label style={styles.label}>
                                <span
                                    style={styles.labelText}
                                >
                                    Email
                                </span>

                                <input
                                    type="email"
                                    name="email"
                                    value={
                                        manualForm.email
                                    }
                                    onChange={
                                        updateManualField
                                    }
                                    placeholder="admin@import"
                                    style={styles.input}
                                />
                            </label>
                        </div>

                        <p style={styles.helpText}>
                            Blank email fields will use
                            admin@import.
                        </p>

                        <div style={styles.sectionTitle}>
                            Opening hours
                        </div>

                        <p style={styles.helpText}>
                            Opening hours are optional. Leave
                            any unknown day blank.
                        </p>

                        <div style={styles.hoursList}>
                            {DAYS.map((day) => (
                                <label
                                    key={day}
                                    style={styles.hoursRow}
                                >
                                    <span style={styles.dayName}>
                                        {day}
                                    </span>

                                    <input
                                        type="text"
                                        value={
                                            manualForm.hours[
                                            day
                                            ]
                                        }
                                        onChange={(event) =>
                                            updateHours(
                                                day,
                                                event.target
                                                    .value
                                            )
                                        }
                                        placeholder="9am - 5:30pm"
                                        style={styles.hoursInput}
                                    />
                                </label>
                            ))}
                        </div>

                        <div style={styles.actions}>
                            <button
                                type="button"
                                onClick={clearManualForm}
                                disabled={manualSaving}
                                style={styles.clearButton}
                            >
                                Clear
                            </button>

                            <button
                                type="submit"
                                disabled={manualSaving}
                                style={{
                                    ...styles.saveButton,
                                    ...(manualSaving
                                        ? styles.disabledButton
                                        : {})
                                }}
                            >
                                {manualSaving
                                    ? 'Adding Shop...'
                                    : 'Add Op Shop'}
                            </button>
                        </div>

                        {manualStatus && (
                            <div
                                style={{
                                    ...styles.status,
                                    ...(manualStatusType ===
                                        'success'
                                        ? styles.successStatus
                                        : {}),
                                    ...(manualStatusType ===
                                        'error'
                                        ? styles.errorStatus
                                        : {})
                                }}
                            >
                                {manualStatus}
                            </div>
                        )}
                    </form>
                )}

                {activeMode === 'bulk' && (
                    <div style={styles.bulkSection}>
                        <button
                            type="button"
                            onClick={downloadTemplate}
                            disabled={uploading}
                            style={styles.templateButton}
                        >
                            Download CSV Template
                        </button>

                        <div style={styles.instructions}>
                            <strong>
                                Supported columns
                            </strong>

                            <code style={styles.codeBlock}>
                                shopId, name, address,
                                contact, email, hours,
                                latitude, longitude, suburb,
                                role, approved
                            </code>

                            <p style={styles.instructionsText}>
                                The hours column can contain a
                                JSON object with the weekday
                                names as keys.
                            </p>
                        </div>

                        <div
                            {...getRootProps()}
                            style={{
                                ...styles.dropzone,
                                ...(isDragActive
                                    ? styles.activeDropzone
                                    : {}),
                                ...(uploading
                                    ? styles.disabledDropzone
                                    : {})
                            }}
                        >
                            <input {...getInputProps()} />

                            {isDragActive ? (
                                <p
                                    style={
                                        styles.dropzoneText
                                    }
                                >
                                    Drop the CSV file here.
                                </p>
                            ) : (
                                <p
                                    style={
                                        styles.dropzoneText
                                    }
                                >
                                    Drag and drop a CSV file
                                    here, or click to select
                                    one.
                                </p>
                            )}
                        </div>

                        {uploading && (
                            <p style={styles.uploadingText}>
                                Uploading shop import...
                            </p>
                        )}

                        {uploadStatus && (
                            <div
                                style={{
                                    ...styles.status,
                                    ...(uploadStatusType ===
                                        'success'
                                        ? styles.successStatus
                                        : {}),
                                    ...(uploadStatusType ===
                                        'error'
                                        ? styles.errorStatus
                                        : {})
                                }}
                            >
                                {uploadStatus}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/* =========================================================
   STYLES
   ========================================================= */

const styles = {
    page: {
        minHeight: '100%',
        padding: '30px 20px',
        background: '#f4f1ea',
        boxSizing: 'border-box'
    },

    container: {
        width: '100%',
        maxWidth: 850,
        margin: '20px auto',
        padding: 30,
        boxSizing: 'border-box',
        background: '#ffffff',
        border: '1px solid #e5ded3',
        borderRadius: 14,
        boxShadow:
            '0 10px 30px rgba(0, 0, 0, 0.07)'
    },

    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 24
    },

    title: {
        margin: 0,
        color: '#234c3d',
        fontSize: 28,
        lineHeight: 1.2
    },

    subtitle: {
        margin: '8px 0 0',
        color: '#686868',
        fontSize: 14,
        lineHeight: 1.5
    },

    tabs: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 7,
        padding: 6,
        marginBottom: 28,
        background: '#f0ede6',
        borderRadius: 10
    },

    tab: {
        padding: '12px 16px',
        border: 'none',
        borderRadius: 7,
        background: 'transparent',
        color: '#555555',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 700
    },

    activeTab: {
        background: '#234c3d',
        color: '#ffffff',
        boxShadow:
            '0 3px 10px rgba(35, 76, 61, 0.20)'
    },

    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: 17
    },

    sectionTitle: {
        marginTop: 4,
        paddingBottom: 8,
        borderBottom: '1px solid #e8e1d7',
        color: '#234c3d',
        fontSize: 17,
        fontWeight: 800
    },

    label: {
        display: 'flex',
        flexDirection: 'column',
        gap: 7
    },

    labelText: {
        color: '#343434',
        fontSize: 13,
        fontWeight: 700
    },

    input: {
        width: '100%',
        padding: '12px 13px',
        boxSizing: 'border-box',
        border: '1px solid #d7d0c5',
        borderRadius: 8,
        outline: 'none',
        background: '#ffffff',
        color: '#222222',
        fontSize: 14
    },

    twoColumns: {
        display: 'grid',
        gridTemplateColumns:
            'repeat(auto-fit, minmax(230px, 1fr))',
        gap: 16
    },

    shopIdPreview: {
        marginTop: -9,
        padding: '8px 10px',
        borderRadius: 6,
        background: '#f5f3ee',
        color: '#69756f',
        fontFamily: 'monospace',
        fontSize: 12
    },

    addressWrapper: {
        position: 'relative'
    },

    addressInputWrapper: {
        position: 'relative'
    },

    addressLoader: {
        position: 'absolute',
        top: '50%',
        right: 14,
        transform: 'translateY(-50%)',
        color: '#234c3d',
        fontSize: 20,
        fontWeight: 800
    },

    suggestions: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        zIndex: 100,
        marginTop: 6,
        overflow: 'hidden',
        border: '1px solid #d9d1c5',
        borderRadius: 9,
        background: '#ffffff',
        boxShadow:
            '0 12px 28px rgba(0, 0, 0, 0.15)'
    },

    suggestionButton: {
        display: 'flex',
        width: '100%',
        flexDirection: 'column',
        gap: 3,
        padding: '12px 14px',
        border: 'none',
        borderBottom: '1px solid #eee9e1',
        background: '#ffffff',
        textAlign: 'left',
        cursor: 'pointer'
    },

    suggestionMain: {
        color: '#253b33',
        fontSize: 13,
        fontWeight: 700
    },

    suggestionSecondary: {
        color: '#747474',
        fontSize: 12
    },

    confirmedAddress: {
        marginTop: -9,
        padding: '9px 11px',
        border: '1px solid #c9ded3',
        borderRadius: 7,
        background: '#f1f8f4',
        color: '#35624f',
        fontSize: 12,
        lineHeight: 1.4
    },

    helpText: {
        margin: '-9px 0 0',
        color: '#777777',
        fontSize: 12,
        lineHeight: 1.4
    },

    hoursList: {
        display: 'flex',
        flexDirection: 'column',
        gap: 9
    },

    hoursRow: {
        display: 'grid',
        gridTemplateColumns: '110px 1fr',
        alignItems: 'center',
        gap: 12
    },

    dayName: {
        color: '#404040',
        fontSize: 13,
        fontWeight: 700
    },

    hoursInput: {
        width: '100%',
        padding: '10px 12px',
        boxSizing: 'border-box',
        border: '1px solid #d7d0c5',
        borderRadius: 7,
        outline: 'none',
        fontSize: 13
    },

    actions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 10,
        marginTop: 10
    },

    clearButton: {
        padding: '12px 18px',
        border: '1px solid #cfc7ba',
        borderRadius: 8,
        background: '#ffffff',
        color: '#555555',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 700
    },

    saveButton: {
        minWidth: 155,
        padding: '12px 18px',
        border: 'none',
        borderRadius: 8,
        background: '#234c3d',
        color: '#ffffff',
        cursor: 'pointer',
        fontSize: 14,
        fontWeight: 800
    },

    disabledButton: {
        opacity: 0.6,
        cursor: 'not-allowed'
    },

    bulkSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20
    },

    templateButton: {
        alignSelf: 'flex-start',
        padding: '10px 15px',
        border: '1px solid #cfc7ba',
        borderRadius: 7,
        background: '#ffffff',
        color: '#234c3d',
        cursor: 'pointer',
        fontWeight: 800
    },

    instructions: {
        padding: 16,
        border: '1px solid #e7e0d5',
        borderRadius: 9,
        background: '#f7f5f0',
        color: '#444444',
        fontSize: 13,
        lineHeight: 1.6
    },

    codeBlock: {
        display: 'block',
        marginTop: 10,
        padding: 10,
        overflowX: 'auto',
        borderRadius: 6,
        background: '#ebe7df',
        color: '#333333',
        whiteSpace: 'normal'
    },

    instructionsText: {
        margin: '10px 0 0'
    },

    dropzone: {
        padding: 44,
        border: '2px dashed #bfa97c',
        borderRadius: 10,
        background: '#fffaf2',
        textAlign: 'center',
        cursor: 'pointer',
        transition:
            'border-color 0.2s ease, background 0.2s ease'
    },

    activeDropzone: {
        borderColor: '#234c3d',
        background: '#eff7f3'
    },

    disabledDropzone: {
        opacity: 0.6,
        cursor: 'not-allowed'
    },

    dropzoneText: {
        margin: 0,
        color: '#555555'
    },

    uploadingText: {
        margin: 0,
        color: '#8a7142',
        fontWeight: 700
    },

    status: {
        marginTop: 5,
        padding: '13px 15px',
        borderRadius: 8,
        background: '#f2f2f2',
        color: '#444444',
        fontSize: 13,
        lineHeight: 1.5
    },

    successStatus: {
        border: '1px solid #b7d8c8',
        background: '#edf8f2',
        color: '#1f6447'
    },

    errorStatus: {
        border: '1px solid #e3b8b8',
        background: '#fff1f1',
        color: '#9b2929'
    }
};

export default ShopImporter;